import { Link } from "react-router-dom";
import { Key, ArrowRight } from "lucide-react";
import { cn } from "@nexirt/ui";

/**
 * AuthWall — inline panel rendered when /v1/chat/message returns 401
 * with `code:"auth_required"` (Rev 25 Phase 3 Loop P3-F).
 *
 * Anonymous visitors hit their 5-message LIFETIME cap → handler returns
 * the auth-wall response → both surfaces (CopilotComposer + ChatWidget)
 * render this in-place. Never a full-page redirect.
 *
 * Copy + CTA are owner-approved per the overnight prompt.
 *
 * `next` query param ensures /portal/login knows where to redirect back
 * to after sign-in completes.
 */
export function AuthWall({
  onDismiss,
  className,
  nextPath = "/",
}: {
  onDismiss: () => void;
  className?: string;
  nextPath?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-copilot-auth-wall
      className={cn(
        "rounded-nx2 border border-nx-accent/40 bg-nx-accent-soft px-4 py-3.5",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] border border-nx-accent/40 bg-nx-bg-raised text-nx-accent">
          <Key className="h-4 w-4" strokeWidth={2} aria-hidden />
        </span>
        <div className="flex-1">
          <p className="text-[13.5px] font-medium text-nx-text">
            You've reached the guest limit.
          </p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-nx-text-dim">
            Sign in to keep chatting with NEXIRT Copilot.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to={`/portal/login?next=${encodeURIComponent(nextPath)}`}
              data-copilot-auth-wall-signin
              className="inline-flex items-center gap-1.5 rounded-nx2 bg-nx-accent px-3 py-1.5 text-[12.5px] font-medium text-white shadow-[var(--nx-shadow-glow)] transition-[transform,filter] duration-150 ease-nx hover:-translate-y-[1px] hover:brightness-110"
            >
              Sign in
              <ArrowRight className="h-3 w-3" strokeWidth={2.2} />
            </Link>
            <button
              type="button"
              onClick={onDismiss}
              data-copilot-auth-wall-dismiss
              className="inline-flex items-center rounded-nx2 border border-nx-line-strong px-3 py-1.5 text-[12.5px] text-nx-text-dim transition-colors hover:bg-nx-surface-2 hover:text-nx-text"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthWall;
